import type { ReactElement, SVGProps } from "react";

/**
 * The icon set.
 *
 * Drawn here rather than pulled from a library, for two reasons. The POC has no
 * dependencies beyond React and adding one for fifteen glyphs is a poor trade.
 * And the stroke weight is matched to the Back Coach mark in BackCoachIcon, so
 * the DSE category icons sit beside the brand mark without looking borrowed.
 *
 * All of them inherit currentColor and a 1.7 stroke at 24px. None of them carry
 * meaning on their own: section 4.4a requires the word to be the carrier, so
 * every icon here sits next to its label rather than replacing it.
 */

type P = SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 18, children, ...rest }: P) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconClock = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </Svg>
);

export const IconCalendar = (p: P) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
  </Svg>
);

export const IconCheck = (p: P) => (
  <Svg {...p}>
    <path d="M4.5 12.5l4.5 4.5L19.5 6.5" />
  </Svg>
);

export const IconSkip = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12h7" />
  </Svg>
);

export const IconPlay = (p: P) => (
  <Svg {...p}>
    <path d="M8.5 6.2l9 5.8-9 5.8V6.2z" />
  </Svg>
);

export const IconPause = (p: P) => (
  <Svg {...p}>
    <path d="M9.5 6.5v11M14.5 6.5v11" />
  </Svg>
);

export const IconLock = (p: P) => (
  <Svg {...p}>
    <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.2" />
    <path d="M8 10.5V8a4 4 0 018 0v2.5" />
  </Svg>
);

export const IconUsers = (p: P) => (
  <Svg {...p}>
    <circle cx="9" cy="8.5" r="3.3" />
    <path d="M3 19.5c0-3.2 2.7-5.2 6-5.2s6 2 6 5.2" />
    <path d="M16 5.6a3.3 3.3 0 010 5.8M17.5 14.8c2.1.6 3.5 2.3 3.5 4.7" />
  </Svg>
);

export const IconTrend = (p: P) => (
  <Svg {...p}>
    <path d="M3.5 16.5l5-5 3.5 3.5 6-6.5" />
    <path d="M14.5 8.5h4v4" />
  </Svg>
);

export const IconReport = (p: P) => (
  <Svg {...p}>
    <path d="M6 3.5h8l4.5 4.5v12.5H6z" />
    <path d="M14 3.5V8h4.5M9 13h6M9 16.5h4" />
  </Svg>
);

export const IconChevronDown = (p: P) => (
  <Svg {...p}>
    <path d="M6 9.5l6 6 6-6" />
  </Svg>
);

/* --- Feedback glyphs ------------------------------------------------------ */

/*
 * Three answers to "How does your back feel now?".
 *
 * Plain strokes of equal weight and length: one rising, one level, one falling.
 * An earlier version added little arrowheads and eased tails to avoid looking
 * like a score, which just made them look like nothing in particular. The
 * clarity matters more, and the bias worry is better answered by giving the
 * three options identical visual weight, which the layout already does.
 */

export const IconEaser = (p: P) => (
  <Svg {...p}>
    <path d="M4 16.5c5-.5 9-4.5 16-9" />
  </Svg>
);

export const IconLevel = (p: P) => (
  <Svg {...p}>
    <path d="M4 12h16" />
  </Svg>
);

export const IconTighter = (p: P) => (
  <Svg {...p}>
    <path d="M4 7.5c5 .5 9 4.5 16 9" />
  </Svg>
);

/* --- DSE category glyphs -------------------------------------------------- */

export const IconMonitor = (p: P) => (
  <Svg {...p}>
    <rect x="2.8" y="4.5" width="18.4" height="12" rx="2" />
    <path d="M9 20h6M12 16.5V20" />
  </Svg>
);

export const IconChair = (p: P) => (
  <Svg {...p}>
    <path d="M7 4.5h10v7H7z" />
    <path d="M5.5 11.5h13M8 11.5v4.5M16 11.5v4.5M6.5 20l1.5-4M17.5 20L16 16" />
  </Svg>
);

export const IconWrist = (p: P) => (
  <Svg {...p}>
    <rect x="2.8" y="9.5" width="18.4" height="7" rx="2" />
    <path d="M6.5 12.5h1M10 12.5h1M13.5 12.5h1M17 12.5h1M8.5 14.8h7" />
  </Svg>
);

export const IconPosture = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="5" r="2.2" />
    <path d="M12 7.4v6.4M12 13.8l-3 6M12 13.8l3 6M8.5 10h7" />
  </Svg>
);

export const IconSpine = (p: P) => (
  <Svg {...p}>
    <path d="M12 3.5v17" />
    <path d="M9.5 6.5h5M9 10h6M9 13.5h6M9.5 17h5" />
  </Svg>
);

export const IconLight = (p: P) => (
  <Svg {...p}>
    <circle cx="12" cy="11" r="4" />
    <path d="M12 2.5v2M12 17.5v2M4.4 11h-2M21.6 11h-2M6.6 5.6L5.2 4.2M18.8 4.2l-1.4 1.4M6.6 16.4l-1.4 1.4M18.8 17.8l-1.4-1.4" />
  </Svg>
);

/** Maps a DSE category key to its glyph. */
export const DSE_ICON: Record<string, (p: P) => ReactElement> = {
  cat1: IconMonitor,
  cat2: IconChair,
  cat3: IconWrist,
  cat4: IconPosture,
  cat5: IconSpine,
  cat6: IconLight,
};
